import React from "react";
import { NoxusInput } from "./InputField";
import { ChangeEvent, useMemo, useState } from "react";
import { SpotSelect } from "./SelectField";
import { Control, Controller } from "react-hook-form";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { GripVertical, Plus, Trash } from "lucide-react";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { NoxusIconButton } from "../UI/NoxusIconButton";
import { Button } from "../shadcn/button";
import { NoxusTooltip } from "../UI/NoxusTooltip";
import { findMaxStringValue } from "@/utils/generic";
import { SpotToast } from "../SpotToast";

function SortableItem({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="w-full bg-white">
      <div className="flex items-end">
        <div className="flex-1">{children}</div>
        <div
          {...attributes}
          {...listeners}
          className="p-3 cursor-grab -mb-0.5 text-primary"
        >
          <GripVertical size="1rem" fill="060C11" strokeWidth="1.25px" />
        </div>
      </div>
    </div>
  );
}

export interface ITypeDictValue {
  label: string;
  type: {
    type: string;
    value: string;
  };
}

interface ITypeDictInputProps {
  values: string[];
  type_map: Record<string, string>;
  label: string;
  subtitle?: string;
  tooltip?: string;
  value: ITypeDictValue[];
  onChange: (value: ITypeDictValue[]) => boolean;
  defaultValue?: { label: string; type: string }[];
  name?: string;
  default_prefix?: string;
  default_value?: string;
  default_type?: string;
  setsConnectorKeys?: boolean;
  min_items?: number;
}

const TypeDictInput = ({
  values,
  type_map,
  label,
  subtitle,
  tooltip,
  value,
  onChange,
  name,
  default_prefix = "New Entry",
  default_value = "Title",
  default_type = "str",
  setsConnectorKeys = false,
  min_items = 1,
}: ITypeDictInputProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [inputs, setInputs] = useState<string[]>(
    value?.map((v) => {
      return v.label;
    }) || []
  );

  const typesOptions = useMemo(() => {
    return values.map((v) => ({ value: v, label: v }));
  }, [values]);

  const addEntry = () => {
    const maxOutputValue = findMaxStringValue(
      value.map((i: any) => i.label),
      new RegExp(`^${default_prefix} (\\d+)$`)
    );
    const name = `${default_prefix} ${maxOutputValue + 1}`;

    if (
      onChange([
        ...value,
        { label: name, type: { value: default_value, type: default_type } },
      ])
    ) {
      setInputs([...inputs, name]);
    } else {
      setInputs([...inputs]);
    }
  };

  const handleDeleteClick = (index: number) => {
    if (value.length <= min_items) return;
    const newInputs = [...inputs];
    newInputs.splice(index, 1);
    setInputs(newInputs);
    if (onChange(value.filter((v, i) => i !== index))) {
      setInputs(newInputs);
    } else {
      setInputs([...inputs]);
    }
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      const new_value = arrayMove(
        value,
        value.findIndex((v) => v.label === active.id),
        value.findIndex((v) => v.label === over.id)
      );

      const new_inputs = arrayMove(
        inputs,
        inputs.findIndex((v) => v === active.id),
        inputs.findIndex((v) => v === over.id)
      );
      onChange(new_value);
      setInputs(new_inputs);
    }
  }

  const onInputChange = (
    newValue: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newInputs = [...inputs];
    newInputs[index] = newValue.currentTarget.value;
    setInputs(newInputs);
  };

  const updateValueOnBlur = (
    event: React.FocusEvent<any, Element>,
    index: number
  ) => {
    const oldINputs = [...value.map((v) => v.label)];

    const changeSuccess = onChange(
      value.map((v, i) => {
        if (i === index) {
          return { ...v, label: inputs[i] };
        }
        return v;
      })
    );

    if (!changeSuccess) {
      setInputs(oldINputs);
    }
  };

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex items-start justify-between mb-1">
        <div>
          <NoxusFormLabel label={label} tooltip={tooltip} name={name} />
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      <div className="flex flex-col w-full mt-3 gap-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={value.map((v) => v.label)}
            strategy={verticalListSortingStrategy}
          >
            {value.map(({ label, type }, index) => {
              return (
                <SortableItem key={label} id={label}>
                  <div key={index} className="w-full flex gap-4 items-center">
                    <div className="flex-1 w-1/2">
                      <NoxusInput
                        value={inputs[index]}
                        onChange={(e) => onInputChange(e, index)}
                        onBlur={(e) => updateValueOnBlur(e, index)}
                        label="Connector name"
                      />
                    </div>
                    {type_map && Object.keys(type_map).length > 1 && (
                      <div className="flex-1 w-1/2">
                        <SpotSelect
                          options={typesOptions}
                          value={typesOptions.find(
                            (o) => o.value === type.value
                          )}
                          onChange={({ value: changeValue }) => {
                            onChange(
                              value.map((v, i) => {
                                if (i === index) {
                                  return {
                                    ...v,
                                    type: {
                                      value: changeValue,
                                      type: type_map[changeValue],
                                    },
                                  };
                                }
                                return v;
                              })
                            );
                          }}
                          label="Type"
                        />
                      </div>
                    )}

                    <div className="self-end">
                      <NoxusTooltip
                        placement="top"
                        content={
                          value.length <= min_items
                            ? `Can't have less than ${min_items} value${min_items !== 1 ? "s" : ""}`
                            : ""
                        }
                      >
                        <NoxusIconButton
                          aria-label="Delete"
                          icon={Trash}
                          variant="smallSecondary"
                          disabled={value.length <= min_items}
                          className="self-end mb-1"
                          onClick={() => handleDeleteClick(index)}
                        />
                      </NoxusTooltip>
                    </div>
                  </div>
                </SortableItem>
              );
            })}
          </SortableContext>
        </DndContext>
      </div>
      <Button onClick={addEntry} className="mt-3 w-full" variant="outline">
        Add {default_prefix}
        <Plus size="1.25rem" strokeWidth="1.25px" />
      </Button>
    </div>
  );
};

interface ITypeDictFieldProps {
  name: string;
  values: string[];
  type_map: Record<string, string>;
  label: string;
  subtitle?: string;
  tooltip?: string;
  defaultValue?: { label: string; type: string }[];
  control: Control<any>;
  onChange?: (value: ITypeDictValue[]) => void;
  isDisabled?: boolean;
  default_prefix?: string;
  default_value?: string;
  default_type?: string;
  setsConnectorKeys?: boolean;
  min_items?: number;
}

export const TypeDictField = ({
  name,
  values,
  type_map,
  label,
  subtitle,
  tooltip,
  defaultValue,
  control,
  onChange,
  isDisabled,
  default_prefix,
  default_value,
  default_type,
  setsConnectorKeys,
  min_items = 1,
}: ITypeDictFieldProps) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      disabled={isDisabled}
      render={({ field }) => {
        const handleOnChange = (value: ITypeDictValue[]) => {
          const parsedLabels = new Set<string>();
          const hasRepeatedNames = value
            .map((v) => v.label)
            .some((label) => {
              if (parsedLabels.has(label)) {
                return true;
              }
              parsedLabels.add(label);
              return false;
            });

          if (setsConnectorKeys && value.some((v) => v.label.includes('"'))) {
            if (onChange) onChange(field.value);
            SpotToast.error("Connector keys are not allowed to have quotes");
            return false;
          }

          if (!hasRepeatedNames) {
            if (onChange) onChange(value);
            field.onChange(value, { shouldValidate: true, shouldDirty: true });

            return true;
          } else {
            // field.value has the old value, meaning it won't be updated
            if (onChange) onChange(field.value);
            return false;
          }
        };
        return (
          <TypeDictInput
            defaultValue={defaultValue}
            values={values}
            type_map={type_map}
            label={label}
            subtitle={subtitle}
            tooltip={tooltip}
            value={field.value}
            onChange={handleOnChange}
            name={name}
            default_prefix={default_prefix}
            default_value={default_value}
            default_type={default_type}
            setsConnectorKeys={setsConnectorKeys}
            min_items={min_items}
          />
        );
      }}
    />
  );
};
